import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import { Objednavka, ObjednavkaDetail, ObjednavkaStav } from '../types';

const STAV_DB: ObjednavkaStav[] = ['new', 'in_fact', 'sent', 'paid', 'finished', 'storned'];

const STAV_WEB: Record<ObjednavkaStav, { txt: string;}> = {
  'new':           { txt: 'Nová' },
  'in_fact':         { txt: 'Poslaná do fakturoidu' },
  'sent':  { txt: 'Poslaná zákazníkovi' },
  'paid':       { txt: 'Zaplacená' },
  'finished':      { txt: 'Dokončená' },
  'storned':     { txt: 'Stornovaná' },
};

const STAV_COLORS: Record<ObjednavkaStav, { bg: string; color: string }> = {
  'new':           { bg: '#d1ecf1', color: '#0c5460' },
  'in_fact':         { bg: '#fff3cd', color: '#856404' },
  'sent':  { bg: '#fff3cd', color: '#856404' },
  'paid':       { bg: '#cce5ff', color: '#004085' },
  'finished':      { bg: '#d4edda', color: '#155724' },
  'storned':     { bg: '#f8d7da', color: '#721c24' },
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('cs-CZ', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatCena = (cena: number | string | null | undefined): string => {
  if (cena === null || cena === undefined || cena === '') return '-';
  const num = Number(cena);
  if (isNaN(num)) return '-';
  if (num === Math.floor(num)) {
    return num.toLocaleString('cs-CZ', { maximumFractionDigits: 0 }) + ' Kč';
  }
  return num.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Kč';
};

const StavBadge: React.FC<{ stav: ObjednavkaStav }> = ({ stav }) => {
  const colors = STAV_COLORS[stav] ?? { bg: '#e9ecef', color: '#333' };
  return (
    <span style={{
      backgroundColor: colors.bg,
      color: colors.color,
      padding: '3px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {/* Zde se nově vypisuje uživatelský text místo databázového klíče */}
      {STAV_WEB[stav]?.txt ?? stav}
    </span>
  );
};

// ─── Modal ───────────────────────────────────────────────────────────────────

interface ObjednavkaModalProps {
  objednavkaId: number;
  onClose: () => void;
  onSaved: (updated: Objednavka) => void;
}

const ObjednavkaModal: React.FC<ObjednavkaModalProps> = ({ objednavkaId, onClose, onSaved }) => {
  const [detail, setDetail] = useState<ObjednavkaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Editable fields
  const [editJmeno, setEditJmeno] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editTelefon, setEditTelefon] = useState('');
  const [editAdresa, setEditAdresa] = useState('');
  const [editPoznamka, setEditPoznamka] = useState('');
  const [editStav, setEditStav] = useState<ObjednavkaStav>('new');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const d = await apiClient.getObjednavkaById(objednavkaId);
        setDetail(d);
        setEditJmeno(d.jmeno);
        setEditEmail(d.email);
        setEditTelefon(d.telefon);
        setEditAdresa(d.adresa ?? '');
        setEditPoznamka(d.poznamka ?? '');
        setEditStav(d.stav);
      } catch (err: any) {
        setError('Nepodařilo se načíst detail objednávky: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [objednavkaId]);

  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.updateObjednavka(detail.id, {
        jmeno: editJmeno,
        email: editEmail,
        telefon: editTelefon,
        adresa: editAdresa || null,
        poznamka: editPoznamka || null,
        stav: editStav,
      });
      setSuccessMsg('Objednávka byla uložena.');
      onSaved({ ...detail, jmeno: editJmeno, email: editEmail, telefon: editTelefon, adresa: editAdresa || null, poznamka: editPoznamka || null, stav: editStav });
    } catch (err: any) {
      setError('Chyba při ukládání: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const labelStyle: React.CSSProperties = { fontWeight: 600, fontSize: '13px', color: '#555', marginBottom: '3px' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#343a40' }}>
            Objednávka #{objednavkaId}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6c757d', lineHeight: 1 }}>×</button>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Načítám...</div>}

        {!loading && error && (
          <div style={{ background: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>{error}</div>
        )}

        {!loading && detail && (
          <>
            {successMsg && (
              <div style={{ background: '#d4edda', color: '#155724', padding: '10px 14px', borderRadius: '6px', marginBottom: '14px', fontSize: '14px' }}>{successMsg}</div>
            )}

            {/* Contact info */}
            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 14px', fontSize: '15px', color: '#495057' }}>Kontaktní údaje</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <div style={labelStyle}>Jméno</div>
                  <input style={inputStyle} value={editJmeno} onChange={e => setEditJmeno(e.target.value)} />
                </div>
                <div>
                  <div style={labelStyle}>E-mail</div>
                  <input style={inputStyle} type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} />
                </div>
                <div>
                  <div style={labelStyle}>Telefon</div>
                  <input style={inputStyle} value={editTelefon} onChange={e => setEditTelefon(e.target.value)} />
                </div>
                <div>
                  <div style={labelStyle}>Adresa</div>
                  <input style={inputStyle} value={editAdresa} onChange={e => setEditAdresa(e.target.value)} placeholder="Adresa zákazníka" />
                </div>
              </div>
            </div>

            {/* Status + date */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div>
                <div style={labelStyle}>Stav objednávky</div>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={editStav}
                  onChange={e => setEditStav(e.target.value as ObjednavkaStav)}
                >
                  {/* Zde mapujeme přes STAVY_DB, hodnota je DB klíč, ale zobrazujeme text z STAV_WEB */}
                  {STAV_DB.map(s => <option key={s} value={s}>{STAV_WEB[s]?.txt || s}</option>)}
                </select>
              </div>
              <div>
                <div style={labelStyle}>Datum objednávky</div>
                <div style={{ padding: '7px 10px', fontSize: '14px', color: '#495057', background: '#fff', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                  {formatDate(detail.datum_objednavky)}
                </div>
              </div>
            </div>

            {/* Variabilní symbol faktury - needitovatelné, výrazné */}
            <div style={{ marginBottom: '20px' }}>
              <div style={labelStyle}>Variabilní symbol</div>
              <div style={{
                padding: '12px 16px',
                fontSize: '20px',
                fontWeight: 700,
                letterSpacing: '0.5px',
                color: '#212529',
                background: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                fontFamily: 'monospace',
              }}>
                {detail.faktura_variabilni_symbol || '—'}
              </div>
            </div>

            {/* Note */}
            <div style={{ marginBottom: '20px' }}>
              <div style={labelStyle}>Poznámka zákazníka</div>
              <textarea
                style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
                value={editPoznamka}
                onChange={e => setEditPoznamka(e.target.value)}
                placeholder="Bez poznámky"
              />
            </div>

            {/* Items */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: '#495057' }}>
                Sběratelské objekty ({detail.polozky.length})
              </h3>
              <div style={{ border: '1px solid #dee2e6', borderRadius: '6px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#495057', color: 'white' }}>
                      <th style={thStyle}>#</th>
                      <th style={thStyle}>ID</th>
                      <th style={thStyle}>Sklad ID</th>
                      <th style={thStyle}>Název</th>
                      <th style={thStyle}>Cena</th>
                      <th style={thStyle}>Rozměry</th>
                      <th style={thStyle}>Váha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.polozky.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>Žádné položky</td></tr>
                    )}
                    {detail.polozky.map((p, idx) => (
                      <tr key={p.id} style={{ borderBottom: '1px solid #dee2e6', background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                        <td style={tdStyle}>{idx + 1}</td>
                        <td style={tdStyle}>{p.sberatelsky_objekt_id}</td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{p.sklad_id}</td>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>{p.nazev || '-'}</td>
                        <td style={tdStyle}>{formatCena(p.cena)}</td>
                        <td style={tdStyle}>{p.rozmery || '-'}</td>
                        <td style={tdStyle}>{p.vaha || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {detail.polozky.length > 0 && (
                <div style={{ textAlign: 'right', padding: '10px 0', fontSize: '15px', fontWeight: 700, color: '#28a745' }}>
                  Celkem: {formatCena(detail.polozky.reduce((s, p) => s + (Number(p.cena) || 0), 0))}
                </div>
              )}
            </div>
            {/* Actions */}
              <button
                onClick={onClose}
                style={{ padding: '8px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
              >
                Zavřít
              </button>          
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ padding: '8px 20px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Ukládám...' : 'Uložit změny'}
              </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const ObjednavkyPage: React.FC = () => {
  const [objednavky, setObjednavky] = useState<Objednavka[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalId, setModalId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [stavFilter, setStavFilter] = useState<ObjednavkaStav | ''>('');

  const fetchObjednavky = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getObjednavky();
      setObjednavky(data);
    } catch (err: any) {
      setError('Nepodařilo se načíst objednávky: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchObjednavky();
  }, [fetchObjednavky]);

  const handleSaved = (updated: Objednavka) => {
    setObjednavky(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o));
  };

  const filtered = objednavky.filter(o => {
    if (stavFilter && o.stav !== stavFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        o.jmeno.toLowerCase().includes(q) ||
        o.email.toLowerCase().includes(q) ||
        String(o.id).includes(q)
      );
    }
    return true;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '24px', color: '#343a40' }}>Objednávky</h1>
        <button
          onClick={fetchObjednavky}
          style={{ padding: '8px 16px', background: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
        >
          Obnovit
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Hledat (jméno, email, ID)..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', minWidth: '250px', flex: 1 }}
        />
        <select
          value={stavFilter}
          onChange={e => setStavFilter(e.target.value as ObjednavkaStav | '')}
          style={{ padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', minWidth: '180px' }}
        >
          <option value="">Všechny stavy</option>
          {/* Zde mapujeme přes STAVY_DB, hodnota je DB klíč, ale zobrazujeme text z STAV_WEB */}
          {STAV_DB.map(s => <option key={s} value={s}>{STAV_WEB[s]?.txt || s}</option>)}
        </select>
      </div>

      {error && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>{error}</div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6c757d' }}>Načítám objednávky...</div>
      )}

      {!loading && (
        <>
          <div style={{ marginBottom: '10px', fontSize: '14px', color: '#6c757d' }}>
            Zobrazeno {filtered.length} z {objednavky.length} objednávek
          </div>

          <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#343a40', color: 'white' }}>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>Jméno</th>
                  <th style={thStyle}>E-mail</th>
                  <th style={thStyle}>Telefon</th>
                  <th style={thStyle}>Adresa</th>
                  <th style={thStyle}>Datum</th>
                  <th style={thStyle}>Variabilní symbol</th>
                  <th style={thStyle}>Stav</th>
                  <th style={{ ...thStyle, width: '80px' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                      {objednavky.length === 0 ? 'Žádné objednávky zatím nebyly vytvořeny.' : 'Žádné objednávky neodpovídají filtru.'}
                    </td>
                  </tr>
                )}
                {filtered.map((o, idx) => (
                  <tr
                    key={o.id}
                    style={{
                      borderBottom: '1px solid #dee2e6',
                      background: idx % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onClick={() => setModalId(o.id)}
                    onMouseEnter={e => (e.currentTarget.style.background = '#e8f4fd')}
                    onMouseLeave={e => (e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f8f9fa')}
                  >
                    <td style={{ ...tdStyle, fontWeight: 600, color: '#495057' }}>#{o.id}</td>
                    <td style={tdStyle}>{o.jmeno}</td>
                    <td style={{ ...tdStyle, color: '#007bff' }}>
                      <a href={`mailto:${o.email}`} onClick={e => e.stopPropagation()} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {o.email}
                      </a>
                    </td>
                    <td style={tdStyle}>{o.telefon}</td>
                    <td style={{ ...tdStyle, color: '#6c757d', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.adresa || '-'}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatDate(o.datum_objednavky)}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{o.faktura_variabilni_symbol || '-'}</td>
                    <td style={tdStyle}><StavBadge stav={o.stav} /></td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button
                        onClick={e => { e.stopPropagation(); setModalId(o.id); }}
                        style={{ padding: '5px 12px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modalId !== null && (
        <ObjednavkaModal
          objednavkaId={modalId}
          onClose={() => setModalId(null)}
          onSaved={(updated) => { handleSaved(updated); }}
        />
      )}
    </div>
  );
};

// ─── Shared styles ────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '13px',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  verticalAlign: 'middle',
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: '10px',
  padding: '28px',
  width: '100%',
  maxWidth: '860px',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};

export default ObjednavkyPage;